
import { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { goalsService } from "@/services/goalsService";
import { Loader2 } from "lucide-react";

interface ReflectionModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (data: ReflectionData) => void;
}

export interface ReflectionData {
  goalId?: string;
  positive: string;
  challenges: string;
}

interface Goal {
  id: string;
  title: string;
  status?: string;
}

const ReflectionModal = ({ isOpen, onOpenChange, onSubmit }: ReflectionModalProps) => {
  const { toast } = useToast();
  const [goalId, setGoalId] = useState<string>("");
  const [positive, setPositive] = useState("");
  const [challenges, setChallenges] = useState("");
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoadingGoals, setIsLoadingGoals] = useState(false);

  // Fetch goals when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchGoals();
      setPositive("");
      setChallenges("");
    }
  }, [isOpen]);

  const fetchGoals = async () => {
    setIsLoadingGoals(true);
    try {
      const response = await goalsService.getGoals();
      const allGoals = response.data || [];
      // Filter to only active goals
      const activeGoals = allGoals.filter((g: Goal) => 
        !g.status || g.status === 'active' || g.status === 'in_progress'
      );
      setGoals(activeGoals);
      if (activeGoals.length > 0) {
        setGoalId(activeGoals[0].id);
      } else {
        setGoalId("");
      }
    } catch (error) {
      console.error("Error fetching goals:", error);
      toast({
        title: "Error",
        description: "Failed to load goals",
        variant: "destructive"
      });
      setGoals([]);
      setGoalId("");
    } finally {
      setIsLoadingGoals(false);
    }
  };

  const handleSubmit = () => {
    if (!positive.trim()) {
      toast({
        title: "Missing information",
        description: "Please share what's going well",
        variant: "destructive"
      });
      return;
    }

    const data: ReflectionData = {
      goalId: goalId || undefined,
      positive,
      challenges
    };

    if (onSubmit) {
      onSubmit(data);
    }
    
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Reflect on Your Goals</DialogTitle>
          <DialogDescription>
            Take a moment to reflect on your progress and challenges.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div>
            <Label htmlFor="goal" className="mb-2 block">
              Goal {goals.length === 0 && !isLoadingGoals && "(Optional)"}
            </Label>
            {isLoadingGoals ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">Loading goals...</span>
              </div>
            ) : goals.length > 0 ? (
              <Select value={goalId} onValueChange={setGoalId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a goal" />
                </SelectTrigger>
                <SelectContent>
                  {goals.map(goal => (
                    <SelectItem key={goal.id} value={goal.id}>
                      {goal.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-sm text-muted-foreground py-2">
                No active goals. You can still submit a reflection without selecting a goal.
              </div>
            )}
          </div>
          
          <div>
            <Label htmlFor="positive" className="mb-2 block">
              What's going well?
            </Label>
            <Textarea
              id="positive"
              value={positive}
              onChange={(e) => setPositive(e.target.value)}
              rows={3}
              placeholder="Share your progress and achievements..."
            />
          </div>
          
          <div>
            <Label htmlFor="challenges" className="mb-2 block">
              What challenges are you facing?
            </Label>
            <Textarea
              id="challenges"
              value={challenges}
              onChange={(e) => setChallenges(e.target.value)}
              rows={3}
              placeholder="What obstacles are you encountering?"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button type="submit" onClick={handleSubmit}>Save Reflection</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReflectionModal;
